import { useState, useMemo } from "react";

// Recommend a FerroFinestra / ALES profile based on user answers.
// value = short profile code used in productSpecs.js; label = display name.
function recommend({ location, glazing, coastal, priority }) {
  if (location === "Internal")
    return {
      value: "W20",
      label: "W20 Steel",
      reason:
        "Internal use — the slim single-glazed W20 profile is the classic choice for partitions, crittall screens and heritage restorations.",
    };
  if (coastal === "Yes")
    return {
      value: "W75TB",
      label: "W75 TB Inox",
      reason:
        "Coastal / marine exposure — the flagship 75mm thermally-broken stainless-steel W75TB Inox profile is engineered for salt air and marine durability.",
    };
  if (priority === "Budget conscious")
    return {
      value: "ALES65",
      label: "ALES65 Aluminium",
      reason:
        "Best value for full-house glazing — our thermally-broken aluminium ALES65 profile mirrors the razor-thin sight-line of steel at aluminium pricing.",
    };
  if (priority === "Thermal performance" || glazing === "Yes")
    return {
      value: "W50TB",
      label: "W50 TB Steel",
      reason:
        "External + double glazed / thermal performance — the 50mm thermally-broken W50TB steel profile balances slim FerroFinestra sight-lines with contemporary energy performance.",
    };
  return {
    value: "W40",
    label: "W40 Steel",
    reason:
      "External use without a thermal-break requirement — the 40mm cold-formed W40 profile takes double glazing and is the versatile everyday external steel choice.",
  };
}

const QUESTIONS = [
  {
    id: "location",
    label: "Where will this be installed?",
    options: ["Internal", "External"],
  },
  {
    id: "glazing",
    label: "Do you need double or triple glazing?",
    options: ["Yes", "No", "Not sure"],
  },
  {
    id: "coastal",
    label: "Is the site coastal or salt-air exposed?",
    options: ["Yes", "No"],
  },
  {
    id: "priority",
    label: "What matters most for this project?",
    options: [
      "Slim sight-lines",
      "Thermal performance",
      "Marine durability",
      "Budget conscious",
    ],
  },
];

export default function ProfileHelper({ availableProfiles = [], onApply, onClose }) {
  const [answers, setAnswers] = useState({});
  const [step, setStep] = useState(0);

  const complete = QUESTIONS.every((q) => answers[q.id]);
  const rec = useMemo(() => (complete ? recommend(answers) : null), [answers, complete]);

  // Match the recommended profile against the profiles actually available for this product
  const matchable = useMemo(() => {
    if (!rec) return null;
    return availableProfiles.find((p) => p.value === rec.value);
  }, [rec, availableProfiles]);

  const setAnswer = (id, val) => {
    setAnswers((a) => ({ ...a, [id]: val }));
    if (step < QUESTIONS.length - 1) setStep(step + 1);
  };

  const reset = () => {
    setAnswers({});
    setStep(0);
  };

  return (
    <div
      data-testid="profile-helper-panel"
      className="mt-3 rounded-sm border border-[#c6a87c]/40 bg-white p-5"
    >
      <div className="flex items-center justify-between border-b border-[#c6a87c]/25 pb-3">
        <p className="font-tech text-[10px] uppercase tracking-[0.28em] text-[#c6a87c]">
          Profile Recommender · {step + 1}/{QUESTIONS.length}
        </p>
        <button
          type="button"
          onClick={onClose}
          data-testid="profile-helper-close"
          className="font-tech text-[10px] uppercase tracking-[0.24em] text-[#0a0a0c]/40 hover:text-[#0a0a0c]"
        >
          Close ×
        </button>
      </div>

      {!complete ? (
        <div className="mt-5">
          <p className="font-serif-display text-lg leading-snug text-[#0a0a0c]">
            {QUESTIONS[step].label}
          </p>
          <div
            className={`mt-4 grid gap-2.5 ${
              QUESTIONS[step].options.length <= 2
                ? "grid-cols-1 sm:grid-cols-2"
                : QUESTIONS[step].options.length === 3
                ? "grid-cols-1 sm:grid-cols-3"
                : "grid-cols-1 sm:grid-cols-2"
            }`}
          >
            {QUESTIONS[step].options.map((opt) => {
              const chosen = answers[QUESTIONS[step].id] === opt;
              return (
                <button
                  key={opt}
                  type="button"
                  data-testid={`profile-helper-${QUESTIONS[step].id}-${opt.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => setAnswer(QUESTIONS[step].id, opt)}
                  className={`min-h-[44px] rounded-sm border px-4 py-3 text-center font-tech text-[11px] font-semibold uppercase leading-tight tracking-[0.12em] transition-all duration-300 ${
                    chosen
                      ? "border-[#c6a87c] bg-[#c6a87c]/10 text-[#0a0a0c]"
                      : "border-[#0a0a0c]/15 text-[#0a0a0c]/80 hover:border-[#c6a87c] hover:text-[#0a0a0c]"
                  }`}
                >
                  {opt}
                </button>
              );
            })}
          </div>

          {/* progress dots */}
          <div className="mt-6 flex items-center gap-2">
            {QUESTIONS.map((q, i) => (
              <button
                key={q.id}
                type="button"
                onClick={() => setStep(i)}
                aria-label={`Step ${i + 1}`}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === step
                    ? "w-8 bg-[#c6a87c]"
                    : answers[q.id]
                    ? "w-4 bg-[#c6a87c]/60"
                    : "w-4 bg-[#0a0a0c]/15"
                }`}
              />
            ))}
            {step > 0 && (
              <button
                type="button"
                onClick={() => setStep(step - 1)}
                data-testid="profile-helper-back"
                className="ml-auto font-tech text-[9px] uppercase tracking-[0.24em] text-[#0a0a0c]/45 hover:text-[#c6a87c]"
              >
                ← Back
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="mt-5" data-testid="profile-helper-recommendation">
          <p className="font-tech text-[10px] uppercase tracking-[0.28em] text-[#c6a87c]">
            Recommended profile
          </p>
          <p className="mt-3 font-serif-display text-2xl leading-tight text-[#0a0a0c]">
            {rec.label}
          </p>
          <p className="mt-3 font-body text-[13px] leading-[1.7] text-[#0a0a0c]/70">
            {rec.reason}
          </p>

          {!matchable && (
            <p className="mt-3 font-body text-[12px] leading-relaxed text-[#c6a87c]">
              Note: this profile isn't offered on the currently selected product.
              Consider a different product from the range for this specification.
            </p>
          )}

          <div className="mt-5 flex flex-wrap gap-2">
            {matchable && (
              <button
                type="button"
                onClick={() => onApply(matchable.value)}
                data-testid="profile-helper-apply"
                className="rounded-full bg-[#c6a87c] px-5 py-2.5 font-tech text-[10px] uppercase tracking-[0.24em] text-[#0a0a0c] transition-colors hover:bg-[#d8bd93]"
              >
                Apply {matchable.label || matchable.value}
              </button>
            )}
            <button
              type="button"
              onClick={reset}
              data-testid="profile-helper-restart"
              className="rounded-full border border-[#0a0a0c]/20 px-5 py-2.5 font-tech text-[10px] uppercase tracking-[0.24em] text-[#0a0a0c]/70 hover:border-[#c6a87c] hover:text-[#0a0a0c]"
            >
              Start over
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
